// ==================================
// Componente Badge
// ==================================

import type { ReactNode } from 'react';

type BadgeVariant = 'success' | 'warning' | 'danger' | 'info' | 'gray' | 'purple';
type BadgeSize = 'sm' | 'md';

interface BadgeProps {
  variant?: BadgeVariant;
  size?: BadgeSize;
  children: ReactNode;
  className?: string;
  dot?: boolean;
}

const variantClasses: Record<BadgeVariant, string> = {
  success: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  warning: 'bg-gold-50 text-gold-700 ring-gold-600/20',
  danger: 'bg-red-50 text-red-700 ring-red-600/20',
  info: 'bg-primary-50 text-primary-700 ring-primary-600/20',
  gray: 'bg-stone-100 text-stone-600 ring-stone-500/20',
  purple: 'bg-primary-50 text-primary-700 ring-primary-600/20',
};

const dotColors: Record<BadgeVariant, string> = {
  success: 'bg-emerald-500',
  warning: 'bg-gold-500',
  danger: 'bg-red-500',
  info: 'bg-primary-500',
  gray: 'bg-stone-400',
  purple: 'bg-primary-500',
};

const sizeClasses: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-xs',
};

export function Badge({
  variant = 'gray',
  size = 'md',
  children,
  className = '',
  dot = false,
}: BadgeProps) {
  return (
    <span
      className={`
        inline-flex items-center gap-1.5 font-medium rounded-full ring-1 ring-inset capitalize
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${className}
      `}
    >
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${dotColors[variant]}`} />}
      {children}
    </span>
  );
}

export default Badge;
